import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { color, ease, font, z } from "../tokens";
import { useLenis } from "../LenisProvider";
import { STATIC_MODE } from "../primitives";

/* Minimal text menu. Paper-coloured text + mix-blend difference keeps it
   legible over both paper and ink sections without a backdrop. At the top
   of the page the four links sit in the open; once the user starts
   scrolling they fold into an animated menu icon that expands on demand,
   so the nav never clashes with section chrome (the mission board's
   topbar especially). */
const Bar = styled.nav`
  position: fixed;
  top: 22px;
  right: clamp(16px, 3vw, 44px);
  z-index: ${z.nav};
  display: flex;
  align-items: center;
  gap: clamp(14px, 2vw, 28px);
  mix-blend-mode: difference;

  @media (max-width: 640px) {
    top: auto;
    bottom: 20px;
    right: 50%;
    transform: translateX(50%);
    gap: 16px;
  }
`;

const NavLink = styled(motion.button)`
  position: relative;
  padding: 4px 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-family: ${font.mono};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${color.paper};
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: ${color.paper};
    transform: scaleX(0);
    transform-origin: right center;
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover::after,
  &:focus-visible::after {
    transform: scaleX(1);
    transform-origin: left center;
  }

  &:focus-visible {
    outline: 1px solid ${color.paper};
    outline-offset: 3px;
  }
`;

const Burger = styled(motion.button)`
  width: 36px;
  height: 36px;
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid rgba(244, 240, 232, 0.55);
  border-radius: 999px;
  background: none;
  cursor: pointer;
  transition: border-color 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover,
  &:focus-visible {
    border-color: ${color.paper};
  }

  &:focus-visible {
    outline: 1px solid ${color.paper};
    outline-offset: 3px;
  }
`;

const Line = styled(motion.span)`
  display: block;
  width: 15px;
  height: 1.5px;
  background: ${color.paper};
  border-radius: 1px;
`;

const ITEMS: { label: string; target: string }[] = [
  { label: "Work", target: "work" },
  { label: "R&D", target: "rnd" },
  { label: "Stack", target: "stack" },
  { label: "Contact", target: "contact" },
];

const Nav: React.FC = () => {
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (STATIC_MODE) return;
    const check = () => {
      const s = window.scrollY > 80;
      setScrolled(s);
      if (!s) setOpen(false);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  const go = (id: string) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) {
      lenis.scrollTo(el, { offset: -24 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const linksShown = STATIC_MODE || !scrolled || open;

  return (
    <Bar aria-label="Site" onMouseLeave={() => setOpen(false)}>
      {ITEMS.map((item, i) => (
        <NavLink
          key={item.target}
          onClick={() => go(item.target)}
          initial={false}
          animate={linksShown ? { opacity: 1, x: 0 } : { opacity: 0, x: 16 }}
          transition={{
            duration: linksShown ? 0.45 : 0.3,
            ease: ease.out,
            delay: linksShown ? (ITEMS.length - 1 - i) * 0.055 : i * 0.03,
          }}
          style={{ pointerEvents: linksShown ? "auto" : "none" }}
          tabIndex={linksShown ? 0 : -1}
        >
          {item.label}
        </NavLink>
      ))}
      <Burger
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        initial={false}
        animate={
          scrolled && !STATIC_MODE
            ? { opacity: 1, scale: 1 }
            : { opacity: 0, scale: 0.6 }
        }
        transition={{ duration: 0.35, ease: ease.out }}
        style={{
          pointerEvents: scrolled && !STATIC_MODE ? "auto" : "none",
        }}
        tabIndex={scrolled && !STATIC_MODE ? 0 : -1}
      >
        <Line
          initial={false}
          animate={open ? { rotate: 45, y: 3.25 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3, ease: ease.out }}
        />
        <Line
          initial={false}
          animate={open ? { rotate: -45, y: -3.25 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3, ease: ease.out }}
        />
      </Burger>
    </Bar>
  );
};

export default Nav;
